import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock4, MapPin, Loader2, ArrowRight, BellRing } from "lucide-react";
import { Button } from "./ui/button";
import GroupSessionsList from "./GroupSessionsList";
import TrainerLine from "./TrainerLine";
import { api } from "../lib/api";
import { scrollToSection } from "../lib/scrollToSection";
import { useCheckoutMode } from "../lib/settings";
import { trackScheduleEnroll, trackScheduleMonthFilter, trackScheduleNotify } from "../lib/tracking";
import {
  FORMAT_LABEL, allGroupsFull, isFull, monthFilters, periodLabel, scheduleLabel, startDayMonth,
  startsInMonth, statusBadge,
} from "../lib/groupFormat";

/**
 * Homepage cohort schedule — the "when does it actually start" section,
 * placed straight after the price so the two questions are answered together.
 *
 * Every open cohort is on screen at once, one under the next. A timetable is
 * read by comparing one column — the start dates and the time slots — against
 * the reader's own week, so the values that get compared are stacked in line
 * with each other rather than spread across a grid. Full-width rows also fill
 * completely at any cohort count, where a three-column grid strands one or two
 * cards alone in the last row as soon as the count is not a multiple of three.
 *
 * The row is 77px against a card's 266px, so the whole list stays shorter than
 * any grid arrangement of the same cohorts, and stays shorter as more open.
 *
 * The card is not the checkout card, deliberately. There the card IS the
 * control: you press it to choose. Here it must contain its own button, and a
 * button inside a button is invalid HTML — the browser hoists it out and the
 * keyboard skips it. The two share their data and their labels, not markup.
 *
 * Whether a row gets its own "enrol" button depends on the checkout mode,
 * because in `online_payment_no_group` the form never asks for a cohort at
 * all — participants state preferences and are assigned one later. A button
 * reading "enrol in Grupa 2" that lands on a form with no Grupa 2 in it is a
 * promise broken within seconds, so that mode gets one general CTA instead.
 */
export default function CourseSchedule() {
  const [groups, setGroups] = useState(null);
  const [failed, setFailed] = useState(false);
  // Which month is being looked at, as "2026-10". Empty means all of them,
  // which is what a visitor who touches nothing gets (23.09.2026). The URL
  // carries it, so a link can open the section already filtered.
  const [month, setMonth] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("luna") || "";
    } catch {
      return "";
    }
  });
  const navigate = useNavigate();
  const { isOnline, requiresGroup, programStatus } = useCheckoutMode();

  useEffect(() => {
    let active = true;
    api
      .get("/groups/active")
      .then((r) => { if (active) setGroups(r.data || []); })
      // A red technical error box in the middle of a marketing page frightens
      // without helping. The visitor is shown the same "we'll let you know"
      // panel as when there genuinely are no cohorts — they still have
      // somewhere to go, and the failure is ours to read in the console.
      .catch(() => { if (active) { setGroups([]); setFailed(true); } });
    return () => { active = false; };
  }, []);

  const months = monthFilters(groups || []);
  // One month is not a filter, it is a label — and the button pair would
  // occupy a row to say what the list already says.
  const showFilters = months.length > 1;
  // A month whose cohorts are gone (archived while somebody had the link)
  // falls back to all of them rather than to an empty section.
  const activeMonth = showFilters && months.some((m) => m.value === month) ? month : "";
  const visibleGroups = (groups || []).filter((g) => startsInMonth(g, activeMonth));

  const chooseMonth = (value) => {
    const next = value === activeMonth ? "" : value;
    setMonth(next);
    trackScheduleMonthFilter(next, (groups || []).filter((g) => startsInMonth(g, next)).length);
    try {
      const url = new URL(window.location.href);
      if (next) url.searchParams.set("luna", next);
      else url.searchParams.delete("luna");
      url.hash = "#grupe";
      window.history.replaceState(null, "", url);
    } catch { /* an address we cannot rewrite must not break the filter */ }
  };

  const paused = programStatus === "paused";
  // Cohorts can be listed and still be unbuyable: every one of them full.
  // With cohorts of 20–25 seats against a list of 270 people, this is the
  // expected end state of the campaign, not an edge case.
  const everythingFull = allGroupsFull(groups || []);
  const showRows = groups !== null && groups.length > 0;
  // Nothing here can be joined today — the notify panel replaces the CTAs.
  // The rows stay up when there are any: a visitor seeing dated cohorts marked
  // full learns that the course runs and is in demand, which "we have no
  // cohorts" would have told them was untrue.
  const nothingBookable = paused || everythingFull || !showRows;
  // Per-row buttons only where the checkout form actually asks for a cohort.
  const perRowCta = !paused && requiresGroup;

  const goToLeadForm = (reason) => {
    trackScheduleNotify(reason);
    scrollToSection("lead-form");
  };

  const enroll = (g) => {
    trackScheduleEnroll(g);
    navigate(`/checkout?grupa=${encodeURIComponent(g.id)}`);
  };

  const renderRow = (g) => {
    // The badge may be absent: a cohort that publishes no counts and is
    // neither nearly full nor full has nothing to say about availability.
    // Fullness is read separately so it cannot go missing with it.
    const badge = statusBadge(g);
    const full = isFull(g);
    const start = startDayMonth(g.start_date);
    return (
      <article
        key={g.id}
        data-testid={`schedule-card-${g.id}`}
        className={[
          "rounded-2xl border bg-white p-5 md:py-4 md:px-5 transition-all",
          "flex flex-col md:flex-row md:items-center gap-4 md:gap-5",
          // A full cohort stays in date order rather than sinking to the
          // bottom: the section promises a timetable, and reordering it by
          // availability breaks the one thing the reader came for.
          full
            ? "border-[#E5E7EB] opacity-60"
            : "border-[#E5E7EB] shadow-sm hover:border-[#965EA7]/60 hover:shadow-md",
        ].join(" ")}
      >
        {/* Start date + identity. A wide row needs something to catch the eye
            at its left edge, and the start date is what people choose on. */}
        <div className="flex items-center gap-4 md:gap-5 md:flex-1 md:min-w-0">
          {start && (
            <div
              className="w-14 shrink-0 rounded-xl bg-[#F0E7F4] py-2 text-center leading-none"
              data-testid={`schedule-date-${g.id}`}
              aria-hidden="true"
            >
              <div className="font-display text-xl font-bold text-[#7A4986]">{start.day}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#965EA7]">
                {start.month}
              </div>
            </div>
          )}
          <div className="min-w-0">
            <h3 className="font-display text-base font-bold text-[#373A45] leading-snug">
              {g.name}
            </h3>
            {/* Written by whoever sets up the group. Rendered as text, never
                as HTML, and `pre-line` keeps the line breaks they typed. */}
            {g.description && g.description.trim() && (
              <p
                className="mt-1 text-sm text-[#373A45]/80 leading-snug whitespace-pre-line break-words"
                data-testid={`schedule-description-${g.id}`}
              >
                {g.description.trim()}
              </p>
            )}
            <div className="mt-0.5 text-xs text-[#6B7280]">
              {periodLabel(g.start_date, g.end_date)}
            </div>
            {/* Who teaches it, when the trainer agreed to be shown (16.10.2026). */}
            <TrainerLine trainer={g.trainer} pending={g.trainer_pending} className="mt-2.5" testid={`schedule-trainer-${g.id}`} />
          </div>
        </div>

        {/* Schedule + format. Given its own width so a long timetable wraps
            onto a second line instead of shoving the button off the row. */}
        <div className="md:flex-[1.4] md:min-w-0 text-sm text-[#373A45]">
          <div className="flex items-start gap-2">
            <Clock4 size={15} className="text-[#3F8EBA] shrink-0 mt-0.5" />
            <span>{scheduleLabel(g)}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[#6B7280]">
            <MapPin size={15} className="text-[#519872] shrink-0" />
            <span>{FORMAT_LABEL[g.format] || g.format}</span>
          </div>
          <GroupSessionsList sessions={g.sessions} testid={`schedule-sessions-${g.id}`} className="mt-1.5" />
        </div>

        {/* One width, whatever this row holds: a badge, a button, both, or
            neither. Sized to the widest of those (badge + button = 219px).
            Letting it size itself is what pulled the schedule beside "Aproape
            plin" 40px out of line with the rows above it. */}
        <div className="flex items-center justify-between md:justify-end gap-3 md:shrink-0 md:w-60">
          <div className="md:flex md:justify-end">
            {badge && (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none whitespace-nowrap ${badge.cls}`}
                data-testid={`schedule-status-${g.id}`}
              >
                {badge.label}
              </span>
            )}
          </div>
          {/* A full cohort gets no button rather than a disabled one repeating
              "Grupă completă" beside a badge that already says it. */}
          {perRowCta && !full && (
            <Button
              onClick={() => enroll(g)}
              data-testid={`schedule-enroll-${g.id}`}
              className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold whitespace-nowrap"
            >
              Înscrie-te
              <ArrowRight size={16} className="ml-1.5" />
            </Button>
          )}
        </div>
      </article>
    );
  };

  /* Nothing can be joined today. Which sentence is true matters: "we have no
     cohorts" in front of dated cohorts marked full would be a plain
     contradiction, and "they are taken" with nothing on screen says nothing. */
  const notifyPanel = (
    <div
      className="mt-10 mx-auto max-w-2xl rounded-3xl border-2 border-[#965EA7]/25 bg-[#F0E7F4]/40 p-8 md:p-10 text-center"
      data-testid="schedule-no-cohorts"
    >
      <div className="mx-auto h-12 w-12 rounded-2xl bg-white border border-[#E9DCEF] text-[#965EA7] grid place-items-center">
        <BellRing size={22} />
      </div>
      <h3 className="mt-5 font-display text-xl md:text-2xl font-bold text-[#373A45]">
        {paused
          ? "Înscrierile sunt temporar pe pauză."
          : everythingFull
          ? "Grupele deschise sunt ocupate momentan."
          : "Momentan nu avem grupe deschise."}
      </h3>
      <p className="mt-3 text-[#4b4f5a] leading-relaxed">
        Dacă dorești, înscrie-te mai jos și revenim cu informații imediat ce
        deschidem grupe noi — cu datele de start și intervalele orare disponibile.
      </p>
      <Button
        onClick={() => goToLeadForm(paused ? "paused" : everythingFull ? "all_full" : "no_cohorts")}
        data-testid="schedule-notify-primary"
        className="mt-6 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold text-base px-8 py-6 shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] hover:-translate-y-0.5 transition-all"
      >
        Anunță-mă când deschideți grupe noi
      </Button>
    </div>
  );

  return (
    <section
      id="grupe"
      className="scroll-mt-28 bg-white border-y border-[#E5E7EB] py-20 md:py-24"
      data-testid="course-schedule"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center rounded-full bg-[#F0E7F4] px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest">
            Orarul grupelor
          </span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold text-[#373A45] !leading-[1.15] tracking-tight">
            Alege grupa care ți se potrivește
          </h2>
          <p className="mt-4 text-base text-[#6B7280] leading-relaxed">
            Toate grupele parcurg aceeași programă acreditată, dar sunt organizate
            preponderent pe categorii de cadre didactice, astfel încât exemplele,
            aplicațiile și discuțiile din sesiunile live să fie cât mai relevante
            pentru activitatea participanților.
          </p>
          <p className="mt-3 text-base text-[#6B7280] leading-relaxed">
            Poți alege, în funcție de disponibilitate și preferințele tale de
            program, dintre grupe orientate în principal către:
          </p>
          {/* The three audiences as chips rather than a "•"-joined sentence:
              on a phone that line wraps mid-category, and these are the words
              a reader is scanning for to find their own group. */}
          <ul
            className="mt-4 flex flex-wrap justify-center gap-2"
            aria-label="Categorii de grupe"
            data-testid="schedule-audiences"
          >
            {["Învățământ Primar", "Discipline STEM", "Limbi și discipline umaniste"].map((label) => (
              <li
                key={label}
                className="rounded-full border border-[#965EA7]/25 bg-[#F0E7F4]/60 px-4 py-1.5 text-sm font-semibold text-[#373A45]"
              >
                {label}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-base text-[#6B7280] leading-relaxed">
            Grupele diferă și prin data de start și intervalul orar al întâlnirilor live.
          </p>
        </div>

        {groups === null ? (
          <div
            className="mt-10 flex items-center justify-center gap-2 text-sm text-[#6B7280]"
            data-testid="schedule-loading"
          >
            <Loader2 size={16} className="animate-spin" /> Se încarcă grupele disponibile…
          </div>
        ) : (
          <>
            {showRows && showFilters && (
              <div
                className="mt-10 -mx-6 px-6 flex gap-2 overflow-x-auto sm:mx-0 sm:px-0 sm:flex-wrap sm:justify-center"
                role="group"
                aria-label="Filtrează grupele după luna de start"
                data-testid="schedule-month-filters"
              >
                {[{ value: "", label: "Toate", count: groups.length }, ...months].map((m) => {
                  const on = m.value === activeMonth;
                  return (
                    <button
                      key={m.value || "all"}
                      type="button"
                      onClick={() => chooseMonth(m.value)}
                      aria-pressed={on}
                      className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                        on
                          ? "border-[#965EA7] bg-[#965EA7] text-white"
                          : "border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]/50 hover:bg-[#F0E7F4]/40"
                      }`}
                      data-testid={`schedule-month-${m.value || "all"}`}
                    >
                      {m.label} <span className="tabular-nums opacity-70">({m.count})</span>
                    </button>
                  );
                })}
              </div>
            )}

            {showRows && (
              <div className={`${showFilters ? "mt-6" : "mt-10"} flex flex-col gap-3`} data-testid="schedule-list">
                {visibleGroups.map(renderRow)}
              </div>
            )}

            {nothingBookable ? (
              notifyPanel
            ) : (
              <>
                {/* In `online_payment_no_group` the rows carry no buttons,
                    because the form never asks for a cohort. This is then the
                    only way forward from the section. */}
                {!perRowCta && (
                  <div className="mt-8 flex justify-center">
                    <Button
                      onClick={() => { trackScheduleEnroll(null); navigate("/checkout"); }}
                      data-testid="schedule-enroll-general"
                      className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold text-base px-8 py-6 shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] hover:-translate-y-0.5 transition-all"
                    >
                      {isOnline ? "Înscrie-te la curs" : "Înscrie-te"}
                      <ArrowRight size={16} className="ml-1.5" />
                    </Button>
                  </div>
                )}

                {/* Secondary zone: one line and a button, for whoever fits none
                    of the intervals above. Deliberately lighter than the rows
                    — it is the fallback, not a competing offer. */}
                <div
                  className="mt-8 rounded-2xl border border-[#E5E7EB] bg-[#F5F6F6] px-6 py-5
                             flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  data-testid="schedule-notify-strip"
                >
                  <p className="text-sm text-[#4b4f5a] leading-relaxed">
                    <strong className="text-[#373A45]">Nu ți se potrivește niciun interval?</strong>{" "}
                    Lasă-ne datele și revenim cu informații imediat ce deschidem grupe noi.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => goToLeadForm("no_fit")}
                    data-testid="schedule-notify-secondary"
                    className="shrink-0 rounded-full border-2 border-[#965EA7] text-[#965EA7] hover:bg-[#965EA7] hover:text-white font-semibold"
                  >
                    <BellRing size={15} className="mr-1.5" /> Anunță-mă
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {failed && (
          /* Not shown to the visitor — a marker for us when checking why the
             panel above appeared on a day cohorts were open. */
          <span className="sr-only" data-testid="schedule-fetch-failed">
            Lista grupelor nu a putut fi încărcată.
          </span>
        )}
      </div>
    </section>
  );
}
